import { useContext, useMemo, useState, useEffect } from "react";
import Step1 from "./Step1";
import Step2 from "./Step2";
import { motion } from "framer-motion";
import { useDispatch, useSelector } from "react-redux";
import { useQueryClient } from "@tanstack/react-query";
import { AIConfigI } from "@/components/integration-modals/OpenAi";
import { BotContext } from "../store";
import { axiosGet, axiosPost } from "@/services/axiosService";
import { userSlice } from "@/redux/User";
import { useNavigate } from "react-router-dom";

import {
  AnthropicIcon,
  CallIcon,
  ChatGptIcon,
  CustomerSupporIcon,
  DeepSeekIcon,
  GeminiIcon,
  GrokIcon,
  GroqIcon,
  OpenRouterIcon,
  OthersIconSvg,
  SmilyIcon,
  UpgradeIcon,
  WebinarIcon,
} from "../components/AiBotAssets";
import { CbbFullLogo } from "@/assets/AiBotAssets";
import ReadyToGoModal from "./ReadyToGoModal";
import { ThreeDotLoader } from "@/pages/preview/ChatComponent";
import { State } from "@/redux/Types";

const CUSTOM_ENHANCE_PROMPT = `You are an expert prompt engineer. Analyze the user's provided content and initial prompt, then create an optimized, clear, and comprehensive prompt that effectively achieves their stated goal for an AI chat agent.`;

const NewCompanyOnboarding = ({
  showConfigModal = false,
  setshowConfigModal = () => {},
  botData = {},
  fetchUrl = async () => {},
  onDataChange = () => {},
  onContinue = () => {},
  onClose = () => {},
  websiteUrl = "",
  setWebsiteUrl = () => {},
  showModelSelection = false,
  train = false,
  setTrain = () => {},
  showRewardSection = false,
  planName = "",
}: {
  showConfigModal?: boolean;
  botData?: any;
  fetchUrl?: () => Promise<void>;
  onDataChange?: (field: string, value: any, isChatReset?: boolean) => void;
  onContinue?: (
    train: boolean,
    differentiatingFactor: string,
    keyAdded?: boolean,
  ) => void;
  onClose?: () => void;
  websiteUrl?: string;
  setWebsiteUrl?: React.Dispatch<React.SetStateAction<string>>;
  showModelSelection?: boolean;
  train?: boolean;
  setTrain?: React.Dispatch<React.SetStateAction<boolean>>;
  setshowConfigModal?: React.Dispatch<React.SetStateAction<boolean>>;
  showRewardSection?: boolean;
  planName?: string;
}) => {
  const navigate = useNavigate();
  const fromOutgrow = useSelector((state: State) => state.user.fromOutgrow);
  const [step, setStep] = useState(showModelSelection ? 2 : 1);
  const [selectedGoalIdx, setselectedGoalIdx] = useState<number | null>(0);
  const [differentiatingFactor, setDifferentiatingFactor] = useState("");
  const [customGoal, setCustomGoal] = useState("");
  const [aiModelEnabled, setAiModelEnabled] = useState(true);
  const [triedSubmit, setTriedSubmit] = useState(false);
  const [selectedAiProviderName, setSelectedAiProviderName] = useState<
    string | null
  >("");
  const [selectedAiModelName, setSelectedAiModelName] = useState<string | null>(
    "",
  );

  const [loader, setLoader] = useState<boolean>(false);
  const [configLoader, setConfigLoader] = useState<boolean>(false);
  const [apiKey, setApiKey] = useState<any>();
  const [apiKeyError, setApiKeyError] = useState("");
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const [allAIConfig, setAllAIConfig] = useState<AIConfigI[] | []>([]);
  const [store, storeDispatch] = useContext(BotContext);
  const [organizationId, setOrganizationId] = useState<any>();
  const [goals, setGoals] = useState<any[]>(
    [].map((category: any) => ({
      ...category,
      goals: category.goals.map((goal) => ({ ...goal, isActive: false })),
    })),
  );
  const [showGoalInputError, setShowGoalInputError] = useState(false);

  const goalsData = botData?.goals?.categories;
  const miscSubCats =
    goalsData?.find((cat) => cat.cat === "Miscellaneous")?.subCat || [];
  const otherIdx = miscSubCats.length;

  useEffect(() => {
    getAllAIConfig();
  }, []);

  const handleSelectGoal = (cat, selectedSubCat, idx) => {
    if (cat === "Miscellaneous") {
      setCustomGoal("");
      onDataChange?.(
        "aiBotConfig.customGoals",
        {
          isEnable: true,
          isAdded: true,
          data: goals.filter((g) => g.cat !== "Custom Goals"),
        },
        true,
      );
    }
    const fullCategory = goalsData.find((c) => c.cat === cat);
    if (!fullCategory) return;
    setGoals((prev) => {
      const updatedPrev = prev.map((g) => ({
        ...g,
        goals: g.goals.map((sub) => ({ ...sub, isActive: false })),
      }));
      const catIdx = updatedPrev.findIndex((g) => g.cat === cat);
      if (catIdx === -1) {
        return [
          ...updatedPrev,
          {
            ...fullCategory,
            goals: [
              {
                ...selectedSubCat,
                content: "",
                isActive: true,
                q_a: (selectedSubCat.q_a || []).map((q) => ({
                  ...q,
                  answer: "",
                })),
              },
            ],
          },
        ];
      } else {
        const category = updatedPrev[catIdx];
        const goalsExists = category.goals.some(
          (sub) => sub.title === selectedSubCat.title,
        );
        if (goalsExists) {
          const updatedCategory = {
            ...category,
            goals: category.goals.map((sub) =>
              sub.title === selectedSubCat.title
                ? { ...sub, isActive: true }
                : { ...sub, isActive: false },
            ),
          };
          const updated = [...updatedPrev];
          updated[catIdx] = updatedCategory;
          return updated;
        }
        const updatedCategory = {
          ...category,
          goals: [
            ...category.goals.map((sub) => ({ ...sub, isActive: false })),
            {
              ...selectedSubCat,
              content: "",
              isActive: true,
              q_a: (selectedSubCat.q_a || []).map((q) => ({
                ...q,
                answer: "",
              })),
            },
          ],
        };
        const updated = [...updatedPrev];
        updated[catIdx] = updatedCategory;
        return updated;
      }
    });
    setselectedGoalIdx(idx);
  };
  const handleQAChange = (cat, idx, value) => {
    setGoals((prev) =>
      prev.map((category) =>
        category.cat === cat
          ? {
              ...category,
              goals: category.goals.map((goal) =>
                goal.isActive
                  ? {
                      ...goal,
                      q_a: goal.q_a.map((qa, i) =>
                        i === idx ? { ...qa, answer: value } : qa,
                      ),
                    }
                  : goal,
              ),
            }
          : category,
      ),
    );
  };

  const handleCustomGoalChange = (value) => {
    setCustomGoal(value);
    onDataChange?.(
      "aiBotConfig.customGoals",
      {
        isEnable: true,
        isAdded: true,
        data: [
          {
            cat: "Custom Goals",
            goals: [{ title: "Custom Goals", content: value, isActive: true }],
          },
        ],
      },
      true,
    );
  };

  const getAllAIConfig = () => {
    setConfigLoader(true);
    axiosGet(`/configurations/allAIConfigs`)
      .then((response) => {
        setAllAIConfig(response.data.data);
        storeDispatch({ type: "set-config", config: response.data.data });
        setStep((prevStep) => {
          if (
            prevStep === 2 &&
            response.data.data.length > 0 &&
            !showModelSelection
          ) {
            return 3;
          }
          return prevStep;
        });
        if (response.data.data.length > 0) {
          setAiModelEnabled(false);
        }

        setConfigLoader(false);
      })
      .catch((err) => {
        setConfigLoader(false);
      });
  };

  const handleSave = async () => {
    try {
      if (!apiKey || !selectedAiModelName) {
        setLoader(false);
        throw new Error("Please Fill the Required Fields");
      }
      const integrationObj = {
        name: `key-${allAIConfig?.length + 1}`,
        model: selectedAiModelName,
        provider: selectedAiProviderName,
        ...(organizationId && { organizationId }),
        apiKey,
      };

      const response = await axiosPost(`/configurations/addAIConfig`, {
        ...integrationObj,
      });

      if (response.status == 201) {
        storeDispatch({
          type: "set-currentCustomModelData",
          curentCustomModelData: response.data,
        });
        dispatch(
          userSlice.actions.setToastPopUp({
            show: true,
            toastType: "success",
            message: "Configuration added successfully!",
            duration: 1500,
          }),
        );
        // getAllAIConfig();
        // setState(0);
        queryClient.invalidateQueries({
          queryKey: ["configurations", "openai"],
        });
        onDataChange?.("aiBotConfig.configId", response.data.saveConfig._id);
        return true;
      }
    } catch (e: any) {
      let message;
      if (e?.response?.data?.statusCode === 401) message = "Invalid API Key";
      else if (e?.response?.data?.statusCode === 404)
        message = "Some error occurred in choosing model";
      setApiKeyError(
        message
          ? message
          : e.response?.data?.message
            ? e.response.data.message
            : e.message,
      );
      setLoader(false);
      return false;
    }
  };

  function sanitizeUrl(url: string) {
    try {
      if (!url) throw new Error("Empty url");

      if (!isNaN(Number(url))) {
        throw new Error("Invalid url");
      }

      if (!url.startsWith("http")) {
        url = "https://" + url;
      }

      let urlObj: URL;
      try {
        urlObj = new URL(url);
      } catch (err) {
        throw new Error("Invalid url");
      }

      if (urlObj.protocol !== "http:" && urlObj.protocol !== "https:") {
        throw new Error("Invalid url");
      }

      if (!urlObj.host.includes(".")) {
        throw new Error("Invalid url");
      }

      return urlObj.origin + urlObj.pathname;
    } catch (err: any) {
      dispatch(
        userSlice.actions.setToastPopUp({
          show: true,
          toastType: "error",
          message: err.message,
          duration: 1500,
        }),
      );
    }
  }

  const isNextEnabled = useMemo(() => {
    if (step === 2) return Boolean(selectedAiProviderName);
    if (!websiteUrl?.trim()) return false;
    if (selectedGoalIdx === otherIdx) return true;
    return selectedGoalIdx !== null || showModelSelection;
  }, [
    websiteUrl,
    selectedGoalIdx,
    step,
    showModelSelection,
    selectedAiProviderName,
    otherIdx,
  ]);

  const validateStep1 = () => {
    if (selectedGoalIdx === otherIdx) {
      if (!customGoal.trim()) {
        setShowGoalInputError(true);
        return false;
      }
      setShowGoalInputError(false);
      return true;
    }
    if (selectedGoalIdx === null) {
      setShowGoalInputError(false);
      return false;
    }
    const miscCategory = goals.find((g) => g.cat === "Miscellaneous");
    const activeGoal = miscCategory?.goals.find((g) => g.isActive);
    if (activeGoal && activeGoal.q_a && activeGoal.q_a.length > 0) {
      const hasEmptyQA = activeGoal.q_a.some(
        (qa) => !qa.answer || !qa.answer.trim(),
      );
      if (hasEmptyQA) {
        setShowGoalInputError(true);
        return false;
      }
    }
    setShowGoalInputError(false);
    if (!sanitizeUrl(websiteUrl)) return false;
    return true;
  };

  const generatePrompt = async () => {
    const miscCategory = goals.find((g) => g.cat === "Miscellaneous");
    const activeGoal = miscCategory?.goals.find((g) => g.isActive);
    if (activeGoal?.title == "Help customers") {
      onDataChange?.("aiBotConfig.customGoals", {
        isEnable: false,
        isAdded: false,
        data: [],
      });
      return;
    }

    if (!activeGoal || !activeGoal.q_a || !activeGoal.prompt) {
      if (!customGoal) return;

      const response = await axiosPost("/ai/enhance-prompt", {
        prompt: customGoal,
        generate: false,
        customEnhancePrompt: CUSTOM_ENHANCE_PROMPT,
      });
      onDataChange?.(
        "aiBotConfig.customGoals",
        {
          isEnable: true,
          isAdded: true,
          data: [
            {
              cat: "Custom Goals",
              goals: [
                {
                  title: "Custom Goals",
                  content: response.data.data.prompt,
                  isActive: true,
                },
              ],
            },
          ],
        },
        true,
      );

      return;
    }
    let systemPrompt = activeGoal.prompt;
    const filteredQA = (activeGoal.q_a || [])
      .filter((item) => item.answer && item.answer.trim() !== "")
      .map(({ placeholder, _id, ...rest }) => rest);
    const questionsAnswersCombined = JSON.stringify(filteredQA);
    const variables = { questionsAnswersCombined };
    const promptToBeGenerated = systemPrompt.replace(
      /\{(\w*)\}/g,
      (_, key) => variables[key] ?? "",
    );
    const response = await axiosPost("/ai/enhance-prompt", {
      prompt: promptToBeGenerated,
      generate: true,
      customEnhancePrompt: CUSTOM_ENHANCE_PROMPT,
    });
    let updatedGoals = goals.map((category) => {
      // Only update the "Miscellaneous" or "Custom Goals" category
      if (category.cat === "Miscellaneous" || category.cat === "Custom Goals") {
        return {
          ...category,
          goals: category.goals.map((goal) =>
            goal.isActive
              ? {
                  ...goal,
                  isActive: true,
                  content: response.data.data.prompt,
                }
              : goal,
          ),
        };
      }
      // Leave other categories untouched
      return category;
    });

    onDataChange?.("aiBotConfig.customGoals", {
      isEnable: true,
      isAdded: true,
      data: updatedGoals,
    });
  };

  async function handleContinue(skip = false) {
    setLoader(true);
    let proceedNext: boolean | undefined = true;
    setTriedSubmit(true);
    if (aiModelEnabled && !skip) {
      if (!apiKey) {
        setApiKeyError("Please enter your API key.");
        setLoader(false); // Ensure loader is off if no API key
        return false;
      } else {
        setApiKeyError("");
      }
      setLoader(true); // Only set loader after API key is present
      proceedNext = await handleSave();
      // setLoader(false);
    }
    if (showModelSelection) {
      if (proceedNext) {
        onContinue?.(train, differentiatingFactor, proceedNext);
        return true;
      }
      return false;
    } else {
      if (!proceedNext) return false;
      await generatePrompt();
      setStep(3);
      return true;
    }
  }

  return step === 3 ? (
    <ReadyToGoModal
      onClose={onClose}
      onBuildBot={async () => {
        onContinue(train, differentiatingFactor);
        onClose();
      }}
      showRewardSection={!!showRewardSection}
    />
  ) : (
    <div className="createbot-modal data-source-modal company-onboarding-modal">
      <div
        className="linkPopup-container flex !max-w-[708px] flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* <div
            className={`close flex-cnt`}
            onClick={() => {
              if (!showModelSelection) onClose();
            }}
          >
            <svg
              width="10"
              height="10"
              viewBox="0 0 10 10"
              fill="#6D6D6D"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M10 1.00714L8.99286 0L5 3.99286L1.00714 0L0 1.00714L3.99286 5L0 8.99286L1.00714 10L5 6.00714L8.99286 10L10 8.99286L6.00714 5L10 1.00714Z"
                fill="#6D6D6D"
              />
            </svg>
          </div> */}
        <>
          <div className="border-b-[rgba(0, 0, 0, 0.10)] fx p-sticky jstfy-sp-btw w-link-header top-0 z-[10] rounded-tl-[20px] rounded-tr-[20px] border-b bg-white px-[30px] py-[20px]">
            {fromOutgrow ? (
              <img
                className="h-[25px]"
                src="https://dlvkyia8i4zmz.cloudfront.net/TwCtBh1fT7SoJdrR8deL_Outgrow_logo_1.svg"
                alt="outgrow-logo"
              />
            ) : (
              <CbbFullLogo />
            )}
            {!showModelSelection && (
              <div className="step-progress flex items-center gap-[6px]">
                <div className="progress flex items-center gap-[2px]">
                  <motion.div
                    className="line first h-[5px] rounded-[100px]"
                    animate={{
                      width: step === 1 ? "32px" : "16px",
                      backgroundColor: step === 1 ? "#fb5f66" : "#0000001a",
                    }}
                    transition={{ duration: 0.3, ease: "easeInOut" }}
                  />
                  {planName === "Lifetime" && (
                    <motion.div
                      className="line first h-[5px] rounded-[100px]"
                      animate={{
                        width: step === 2 ? "32px" : "16px",
                        backgroundColor: step === 2 ? "#fb5f66" : "#0000001a",
                      }}
                      transition={{ duration: 0.3, ease: "easeInOut" }}
                    />
                  )}
                </div>
                {planName === "Lifetime" && (
                  <p className="text-center text-[12px] font-normal not-italic leading-none text-[#666]">
                    {step}/2
                  </p>
                )}
              </div>
            )}
          </div>
          <div className="linkPopup-body fx fx-d-col gap-10 overflow-auto bg-white">
            {step === 1 && (
              <Step1
                botData={botData}
                fetchUrl={fetchUrl}
                onDataChange={onDataChange}
                websiteUrl={websiteUrl}
                setWebsiteUrl={setWebsiteUrl}
                differentiatingFactor={differentiatingFactor}
                setDifferentiatingFactor={setDifferentiatingFactor}
                selectedGoalIdx={selectedGoalIdx}
                setselectedGoalIdx={setselectedGoalIdx}
                customGoal={customGoal}
                goals={goals}
                setGoals={setGoals}
                handleSelectGoal={handleSelectGoal}
                handleQAChange={handleQAChange}
                train={train}
                setTrain={setTrain}
                handleCustomGoalChange={handleCustomGoalChange}
                showGoalInputError={showGoalInputError}
                setShowGoalInputError={setShowGoalInputError}
              />
            )}
            {step === 2 && (
              <Step2
                aiModelEnabled={aiModelEnabled}
                setAiModelEnabled={setAiModelEnabled}
                selectedAiProviderName={selectedAiProviderName}
                setSelectedAiProviderName={setSelectedAiProviderName}
                setSelectedAiModelName={setSelectedAiModelName}
                selectedAiModelName={selectedAiModelName}
                apiKey={apiKey}
                setApiKey={setApiKey}
                setApiKeyError={setApiKeyError}
                error={apiKeyError}
              />
            )}
          </div>
          <div className="wd-100 p-sticky flex-cnt bottom-0 gap-20 rounded-bl-[20px] rounded-br-[20px] border-t border-[rgba(0,0,0,0.10)] bg-white p-[20px]">
            <div></div>
            {step !== 1 && (
              <button
                // disabled={showModelSelection}
                className="flex items-center justify-center rounded-[8px] border border-[#FB5F66] bg-[#FFFFFF] px-[30px] py-[10px] text-[#FB5F66] transition-colors duration-200 hover:bg-[#FB5F66] hover:text-white"
                onClick={() => {
                  if (showModelSelection) {
                    showConfigModal ? onClose() : navigate("/dashboard");
                  } else {
                    step === 1 ? onClose() : setStep(step - 1);
                  }
                }}
              >
                {showConfigModal
                  ? "Cancel"
                  : showModelSelection
                    ? "Back to dashboard"
                    : "Back"}
              </button>
            )}
            <button
              className={`continue-btn flex items-center justify-center rounded-[8px] border border-[#FB5F66] bg-[#FB5F66] px-[30px] py-[10px] text-white transition-colors duration-200 hover:border-[#FB5F66] hover:bg-white hover:text-[#FB5F66] ${showModelSelection ? "w-[183px]" : ""} ${!isNextEnabled ? "pointer-events-none cursor-not-allowed opacity-50" : ""} ${step === 2 && loader && !apiKeyError ? "pointer-events-none" : ""}`}
              disabled={!isNextEnabled}
              onClick={async () => {
                if (planName !== "Lifetime") {
                  if (!validateStep1()) return;
                  const result = await handleContinue(true);
                  if (result) {
                    setshowConfigModal(false);
                  }
                } else {
                  if (step === 2) {
                    const result = await handleContinue();
                    if (result) {
                      setshowConfigModal(false);
                    }
                  } else {
                    if (!validateStep1()) return;
                    // If moving from step 1 to step 2, but config exists, skip to step 3
                    if (
                      step === 1 &&
                      allAIConfig.length > 0 &&
                      !showModelSelection
                    ) {
                      handleContinue();
                    } else {
                      setStep(step + 1);
                    }
                  }
                }
              }}
            >
              {step == 2 || planName !== "Lifetime" ? (
                loader && !apiKeyError ? (
                  <p className="exporting-chat continuing-loader flex items-baseline">
                    {" "}
                    Continuing <ThreeDotLoader />
                  </p>
                ) : (
                  "Continue"
                )
              ) : loader ? (
                "Next..."
              ) : (
                "Next"
              )}
            </button>
            {!showModelSelection && step === 2 && (
              <span
                className="absolute right-[20px] cursor-pointer text-[14px] leading-[130%] text-[#fb5f66] underline"
                onClick={async () => {
                  setSelectedAiProviderName(null);
                  setSelectedAiModelName("");
                  setApiKey("");
                  const result = await handleContinue(true);
                  if (result) {
                    setshowConfigModal(false);
                  }
                }}
              >
                Skip this step
              </span>
            )}
          </div>
        </>
      </div>
    </div>
  );
};

export default NewCompanyOnboarding;

export const goals = [
  {
    id: 1,
    heading: "Schedule a Call",
    subHeading: "Book a meeting with the team.",
    icon: <CallIcon />,
  },
  {
    id: 2,
    heading: "Register for a webinar",
    subHeading: "Sign up for an upcoming webinar.",
    icon: <WebinarIcon />,
  },
  {
    id: 3,
    heading: "Signup for Free Trial",
    subHeading: "Start a free product trial",
    icon: <SmilyIcon />,
  },
  {
    id: 4,
    heading: "Promote Special Offer",
    subHeading: "Showcase your latest promotion.",
    icon: <UpgradeIcon />,
  },
  {
    id: 5,
    heading: "Help customers",
    subHeading: "Provide custom support",
    icon: <CustomerSupporIcon />,
  },
  {
    id: 6,
    heading: "Other",
    subHeading: "Share your goal",
    icon: <OthersIconSvg />,
  },
];
export const aiProviders = [
  { id: 1, icon: <ChatGptIcon />, name: "OpenAI" },
  { id: 2, icon: <GeminiIcon />, name: "Gemini" },
  { id: 3, icon: <AnthropicIcon />, name: "Anthropic" },
  // { id: 4, icon: <DeepSeekIcon />, name: "DeepSeek" },
  // { id: 5, icon: <GrokIcon />, name: "Grok" },
  { id: 6, icon: <OpenRouterIcon />, name: "OpenRouter" },
  // {id: 7, icon: <GroqIcon />, name: "Groq",}
];
